import { useEffect, useRef } from "react";

const GOLD = "185, 162, 118";
const GOLD_BRIGHT = "216, 192, 142";

export default function HeroCanvas({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let raf = 0;
    let w = 0;
    let h = 0;
    let routes = [];
    let focal = { x: 0.72, y: 0.5 };
    let target = { x: 0.72, y: 0.5 };

    const bez = (p0, p1, p2, t) => {
      const a = (1 - t) * (1 - t);
      const b = 2 * (1 - t) * t;
      const c = t * t;
      return { x: a * p0.x + b * p1.x + c * p2.x, y: a * p0.y + b * p1.y + c * p2.y };
    };

    const build = () => {
      routes = [];
      const N = 26;
      for (let i = 0; i < N; i++) {
        const side = i % 3;
        let s;
        if (side === 0) s = { x: -0.04, y: Math.random() * 1.2 - 0.1 };
        else if (side === 1) s = { x: Math.random() * 0.85, y: -0.05 };
        else s = { x: Math.random() * 0.85, y: 1.05 };
        const c = {
          x: s.x + (0.72 - s.x) * (0.3 + Math.random() * 0.35),
          y: s.y + (0.5 - s.y) * (0.2 + Math.random() * 0.55) + (Math.random() - 0.5) * 0.55,
        };
        routes.push({
          s,
          c,
          width: 0.5 + Math.random() * 0.9,
          alpha: 0.16 + Math.random() * 0.3,
          dash: [3 + Math.random() * 10, 8 + Math.random() * 16],
          speed: 12 + Math.random() * 30,
          phase: Math.random() * 1000,
          particles: [0, 1].map(() => ({
            t: Math.random(),
            v: 0.0016 + Math.random() * 0.004,
            r: 0.8 + Math.random() * 1.4,
          })),
        });
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (now) => {
      ctx.clearRect(0, 0, w, h);
      focal.x += (target.x - focal.x) * 0.04;
      focal.y += (target.y - focal.y) * 0.04;
      const F = { x: focal.x * w, y: focal.y * h };
      const time = now / 1000;

      for (const r of routes) {
        const e = { x: F.x, y: F.y };
        const s = { x: r.s.x * w, y: r.s.y * h };
        const c = { x: r.c.x * w, y: r.c.y * h };
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.quadraticCurveTo(c.x, c.y, e.x, e.y);
        ctx.strokeStyle = `rgba(${GOLD}, ${r.alpha})`;
        ctx.lineWidth = r.width;
        ctx.setLineDash(r.dash);
        ctx.lineDashOffset = -(time * r.speed + r.phase);
        ctx.stroke();

        for (const p of r.particles) {
          p.t += p.v;
          if (p.t > 1) p.t = 0;
          const pos = bez(s, c, e, p.t);
          const g = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, p.r * 4);
          g.addColorStop(0, `rgba(${GOLD_BRIGHT}, 0.9)`);
          g.addColorStop(1, `rgba(${GOLD_BRIGHT}, 0)`);
          ctx.setLineDash([]);
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, p.r * 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "rgba(255, 244, 214, 0.95)";
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, p.r * 0.7, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.setLineDash([]);

      const pulse = 1 + Math.sin(time * 1.4) * 0.15;
      const R = Math.min(w, h) * 0.14 * pulse;
      const glow = ctx.createRadialGradient(F.x, F.y, 0, F.x, F.y, R);
      glow.addColorStop(0, "rgba(249, 240, 216, 0.95)");
      glow.addColorStop(0.25, `rgba(${GOLD_BRIGHT}, 0.55)`);
      glow.addColorStop(1, `rgba(${GOLD_BRIGHT}, 0)`);
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(F.x, F.y, R, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(255, 252, 240, 0.98)";
      ctx.beginPath();
      ctx.arc(F.x, F.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    };

    const loop = (now) => {
      draw(now);
      raf = requestAnimationFrame(loop);
    };

    resize();
    build();
    if (reduced) draw(0);
    else raf = requestAnimationFrame(loop);

    const ro = new ResizeObserver(() => {
      resize();
      if (reduced) draw(0);
    });
    ro.observe(canvas);

    const parent = canvas.parentElement;
    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = (e.clientX - rect.left) / rect.width;
      const my = (e.clientY - rect.top) / rect.height;
      target = { x: 0.72 + (mx - 0.5) * 0.05, y: 0.5 + (my - 0.5) * 0.06 };
    };
    const onLeave = () => {
      target = { x: 0.72, y: 0.5 };
    };
    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
